import { gsap } from 'gsap'
import { Component } from '../core/smpl-components'
import { Accordion } from '../modules/accordion'

// -----------------------------------------------------------------------------
// Events list — accordion over the event rows, one open at a time, with the
// open row's cover image taking over the preview slot on desktop.
//
// The cover already lives inside the row (it is what mobile shows when the row
// opens), so the preview clones that figure rather than rendering a second set
// of images: same srcset, served from cache.
// -----------------------------------------------------------------------------

export default class extends Component {
	// Marking empty rows has to happen before the accordion is constructed in
	// mount(), so it reads `is-disabled` when it wires up the headers.
	prepare() {
		this.elPreview = this.element.querySelector('.event-preview__active')

		this.element.querySelectorAll('.event-row').forEach((row) => {
			const body = row.querySelector('.event-row__body')

			// Some events carry no description and no cover — there is nothing
			// to open, so take the row out of the accordion rather than flipping
			// the arrow on an empty body.
			if (body && !this.hasContent(body)) {
				row.classList.add('is-disabled')
			}
		})
	}

	hasContent(body) {
		return body.textContent.trim().length > 0 || !!body.querySelector('img, figure, iframe')
	}

	mount() {
		this.accordion = new Accordion(this.element, {
			rowClass: '.event-row',
			headerClass: '.event-row__header',
			bodyClass: '.event-row__body',
			activeClass: 'is-expanded',
			singleOpen: true,
			onChanged: (row, isExpanded) => this.setPreview(isExpanded ? row : null),
		})

		// pointerover rather than pointerenter: it bubbles, so one listener
		// covers every row, and on touch it fires as the finger lands. focusin
		// covers the keyboard route onto the header.
		this.bndOnIntent = this.onIntent.bind(this)
		this.element.addEventListener('pointerover', this.bndOnIntent)
		this.element.addEventListener('focusin', this.bndOnIntent)
	}

	onIntent(ev) {
		this.warmCover(ev.target.closest?.('.event-row'))
	}

	// The cover is loading="lazy" inside a clipped, zero-height body, so it
	// never starts downloading on its own: the fetch and decode would otherwise
	// land in the middle of the expand and hitch it. Warm it as soon as the
	// user shows any intent to open the row.
	warmCover(row) {
		const img = row?.querySelector('.event-row__body__cover img')
		if (!img || img.dataset.warmed) return

		img.dataset.warmed = '1'
		// Flipping lazy -> eager is what actually starts the fetch; decode()
		// then has the bitmap ready so the paint costs nothing mid-animation.
		img.loading = 'eager'
		img.decode?.().catch(() => {})
	}

	// Passing null clears the slot, revealing the default image underneath.
	setPreview(row) {
		if (!this.elPreview) return

		const cover = row?.querySelector('.event-row__body__cover figure')

		// singleOpen reports collapsed-then-expanded within the same tick, so
		// killing the in-flight tween lets the second call win before a frame
		// renders — switching rows never flashes the default image in between.
		gsap.killTweensOf(this.elPreview)

		gsap.to(this.elPreview, {
			opacity: 0,
			duration: 0.2,
			onComplete: () => {
				this.elPreview.replaceChildren()
				if (!cover) return

				this.elPreview.appendChild(cover.cloneNode(true))
				gsap.to(this.elPreview, { opacity: 1, duration: 0.3 })
			},
		})
	}

	unmount() {
		this.element.removeEventListener('pointerover', this.bndOnIntent)
		this.element.removeEventListener('focusin', this.bndOnIntent)

		if (this.accordion) {
			this.accordion.destroy()
			this.accordion = null
		}
	}
}
